import React, { useState, useEffect } from 'react';
import { Loader2, UserCog, Trash2, ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';
import MyLayout from './Layout';
import { useMyAuth } from '../../context/MyAuthContext';
import { apiFetchJson } from './api';

const ROLES = ['client', 'editeur', 'manager', 'admin', 'superadmin'];
const ROLE_LABELS = {
  client: 'Client',
  editeur: 'Éditeur',
  manager: 'Manager',
  admin: 'Administrateur',
  superadmin: 'Super Admin',
};
const ROLE_COLORS = {
  client: 'text-gray-400 bg-gray-500/10',
  editeur: 'text-blue-400 bg-blue-500/10',
  manager: 'text-purple-400 bg-purple-500/10',
  admin: 'text-amber-400 bg-amber-500/10',
  superadmin: 'text-[#0099D6] bg-[#0099D6]/10',
};

function roleLevel(role) {
  return ROLES.indexOf(role);
}

export default function MyUsers() {
  const { user: currentUser } = useMyAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const isSuper = currentUser?.role === 'superadmin';

  const fetchUsers = async () => {
    setLoading(true);
    try {
      setUsers(await apiFetchJson('/api/my/users'));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const showFeedback = (msg, type = 'success') => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiFetchJson(`/api/my/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      }, 'Erreur de mise à jour');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      showFeedback('Rôle mis à jour');
    } catch (err) {
      showFeedback(err.message || 'Erreur réseau', 'error');
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Supprimer l'utilisateur ${email} ?`)) return;
    try {
      await apiFetchJson(`/api/my/users/${userId}`, { method: 'DELETE' }, 'Erreur de suppression');
      setUsers(prev => prev.filter(u => u.id !== userId));
      showFeedback('Utilisateur supprimé');
    } catch (err) {
      showFeedback(err.message || 'Erreur réseau', 'error');
    }
  };

  const canEditUser = (targetUser) => {
    if (currentUser?.email === targetUser.email) return false;
    if (targetUser.role === 'superadmin') return false;
    if (!isSuper && roleLevel(targetUser.role) >= roleLevel('admin')) return false;
    return true;
  };

  const availableRoles = (targetUser) => {
    return ROLES.filter(r => {
      if (r === 'superadmin') return false;
      if (!isSuper && roleLevel(r) >= roleLevel('admin')) return false;
      return true;
    });
  };

  return (
    <MyLayout title="Utilisateurs">
      {/* Feedback */}
      {feedback && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-medium ${
          feedback.type === 'error'
            ? 'bg-red-500/20 border border-red-500/30 text-red-300'
            : 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300'
        }`}>
          {feedback.type === 'error'
            ? <AlertCircle className="w-4 h-4" />
            : <CheckCircle className="w-4 h-4" />}
          {feedback.msg}
        </div>
      )}

      <div className="space-y-4">
        <p className="text-gray-500 text-sm">
          {users.length} utilisateur{users.length !== 1 ? 's' : ''} inscrit{users.length !== 1 ? 's' : ''}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#0099D6] animate-spin" />
          </div>
        ) : (
          <div className="bg-[#12141a] border border-white/5 rounded-2xl overflow-hidden">
            {users.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-12">Aucun utilisateur</p>
            ) : (
              <div className="divide-y divide-white/5">
                {users.map(u => (
                  <div key={u.id} className="flex items-center gap-4 px-5 py-4">
                    {/* Avatar */}
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-9 h-9 rounded-full shrink-0" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#0099D6]/10 flex items-center justify-center text-[#0099D6] text-sm font-bold shrink-0">
                        {(u.name || u.email || '?')[0].toUpperCase()}
                      </div>
                    )}

                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{u.name || '—'}</p>
                      <p className="text-gray-500 text-xs truncate">{u.email}</p>
                    </div>

                    {/* Rôle */}
                    <div className="shrink-0">
                      {canEditUser(u) ? (
                        <div className="relative">
                          <select
                            value={u.role}
                            onChange={e => handleRoleChange(u.id, e.target.value)}
                            className={`appearance-none pl-2 pr-7 py-1 rounded-full text-xs font-semibold cursor-pointer border-0 outline-none ${ROLE_COLORS[u.role] || ROLE_COLORS.client}`}
                          >
                            {availableRoles(u).map(r => (
                              <option key={r} value={r} className="bg-[#12141a] text-white">
                                {ROLE_LABELS[r]}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60" />
                        </div>
                      ) : (
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ROLE_COLORS[u.role] || ROLE_COLORS.client}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="shrink-0">
                      {canEditUser(u) && (
                        <button
                          onClick={() => handleDelete(u.id, u.email)}
                          className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </MyLayout>
  );
}
